//------------------------------------
//           SETUP
//------------------------------------
import React, { useState, useEffect, useRef, useReducer, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // Use useLocation to get the passed state
import { profileDefinitions } from './profileDefinitions'; // Import employee profiles
import './App.css'; // Or the correct path to your CSS file
import TooltipWrapper from './TooltipWrapper';
import { LightBulbIcon } from '@heroicons/react/solid';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Tooltip from '@mui/material/Tooltip';
import io from 'socket.io-client';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  CircularProgressbarWithChildren,
  buildStyles
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { ChevronDown } from "lucide-react";

const initialState = { finalTranscript: '', displayedText: '', lastInterim: '', isMessageBeingProcessed: false };

const gaugeTooltips = {
  Emotion: "The emotion gauge is a measure of how negative and emotionally charged the employee is feeling during the conversation. The higher the value, the more emotionally charged or negative the employee may feel, such as stress, frustration, or anger. Low values reflect positive emotion or low intensity emotion such as happiness, contentment, calm, etc.",
  Autonomy: "The autonomy gauge is a measure of the employee's sense of control, choice, and volition. Low autonomy may indicate the employee is feeling imposed or manipulated, disagreement with the situation, micromanaged, or lacking control of the situation being discussed.",
  Competence: "The competence gauge reflects the employee's sense of learning and growth, as well as their capability to apply their skills effectively for the situation being discussed. Low competence might indicate confusion, feeling stifled, lack of confidence, or inability to effectively respond in the conversation or related to the situation.",
  Relatedness: "The relatedness gauge captures the employee's sense of connection and belonging with you and others related to the situation being discussed. It also captures the employee's sense of connection with others or a higher purpose or mission. Low relatedness can suggest isolation or disconnection. High relatedness reflects feelings of belonging, being valued, or having shared purpose."
};

const isBrowserSTTCompatible = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isCompatible = ('webkitSpeechRecognition' in window) && (ua.includes('chrome') || ua.includes('edg'));
  console.log('🧪 Browser STT compatible?', isCompatible, '| UA:', ua);
  return isCompatible;
};

// Define ProfileTooltip without export
function ProfileTooltip({ profileKey }) {
  return (
    <Tooltip title={<span style={{ fontSize: '1rem' }}>{profileDefinitions[profileKey]}</span>} arrow>
      <LightBulbIcon
        style={{
          width: '18px',
          height: '18px',
          marginLeft: '5px',
          cursor: 'pointer',
          color: '#f59e0b',          // Tailwind amber-500
          backgroundColor: '#fef9c3', // Tailwind yellow-100
          borderRadius: '50%',
          padding: '2px',
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      />
    </Tooltip>
  );
}

function GaugeLabelWithTooltip({ label }) {
  return (
    <TooltipWrapper
      title={<span style={{ fontSize: '.875rem' }}>{gaugeTooltips[label]}</span>}
      arrow
    >
      <span className="cursor-help">{label}</span>
    </TooltipWrapper>
  );
}

//---------------------------------
// Reducer Function for STT
//---------------------------------
function reducer(state, action) {
  switch (action.type) {
    case 'append-final':
      if (state.isMessageBeingProcessed) return state; // 🚫 Block updates while processing
      
      // Append final text, which is always considered complete and overwrites interim
      const newFinalTranscript = `${state.finalTranscript} ${action.payload.trim()} `;
      return { ...state, finalTranscript: newFinalTranscript, displayedText: newFinalTranscript, lastInterim: '' };

    case 'update-interim':
      if (state.isMessageBeingProcessed) return state; // 🚫 Block updates while processing

      // Remove periods, commas, and question marks from both the last known interim and the new interim for comparison
      const regex = /[.,?]/g;  // Regex to match periods, commas, and question marks
      const cleanLastInterim = state.lastInterim.replace(regex, '');
      const cleanNewInterim = action.payload.replace(regex, '');
      const isNewUpdate = cleanNewInterim.startsWith(cleanLastInterim) && cleanNewInterim !== cleanLastInterim;
      const newInterim = isNewUpdate ? action.payload : state.lastInterim;
      return { ...state, displayedText: `${state.finalTranscript} ${newInterim}`, lastInterim: newInterim };
  
    case 'edit':
      // Directly update displayedText with the new value from the textarea
      return { ...state, finalTranscript: action.payload, displayedText: action.payload };

    case 'set-processing-flag': // ✅ New case
      return {
        ...state,
        isMessageBeingProcessed: action.payload,
      };

    case 'reset':
      return {
        ...state,
        displayedText: '',
        lastInterim: '',
      };

    default:
      throw new Error('Unhandled action type: ' + action.type);
      //return state;
  }
}

function Simulator() {
  const [conversation, setConversation] = useState([]);
  const [statusMessage, setStatusMessage] = useState(''); // To display the status of messages being sent
  const chatEndRef = useRef(null); // Ref for controlling scroll to bottom of the chat
  const [leftWidth, setLeftWidth] = useState(50); // Initial width of left pane
  const [isResizing, setIsResizing] = useState(false);
  const location = useLocation(); // Get the location object
  const navigate = useNavigate(); // Define navigate hook at the top level of your component
  const textareaRef = useRef(null);

  const [audioUrl, setAudioUrl] = useState('');
  const [isTtsEnabled, setIsTtsEnabled] = useState(true); // Default to true if you want TTS on by default
  const [isTtsPlaying, setIsTtsPlaying] = useState(false); // 🔇 Used to suppress mic while TTS audio is playing
  const ttsPlayingRef = useRef(false);
  const sttPausedForTtsRef = useRef({ socket: false, browser: false });
  const micWasOnAtSendRef = useRef(false);

  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLoadingScenario, setIsLoadingScenario] = useState(true);
  const [scenarioData, setScenarioData] = useState(null);
  const [isRestartModalOpen, setIsRestartModalOpen] = useState(false);
  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [isNoConversationModalOpen, setIsNoConversationModalOpen] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const mediaRecorderRef = useRef(null);  // Use useRef to persist the MediaRecorder instance
  const socketRef = useRef(null);
  const micStreamRef = useRef(null); // 🔊 holds the active getUserMedia stream used by MediaRecorder
  const levelStreamRef = useRef(null);    // mic meter only
  const socketTimedOutDuringTtsRef = useRef(false);

  const [isStreamReady, setIsStreamReady] = useState(false);

  const [firstName, setFirstName] = useState('');

  const [audioLevel, setAudioLevel] = useState(0);

  const [isTyping, setIsTyping] = useState(false);
  const [typingDots, setTypingDots] = useState('');

  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);

  const [userId, setUserId] = useState(null); // Initialize userId as null first

  const [showContext, setShowContext] = useState(true);
  const [showIssues, setShowIssues] = useState(true);

  const [trustLevels, setTrustLevels] = useState(null);
  const [showDisposition, setShowDisposition] = useState(true);
  const [dispositionLevels, setDispositionLevels] = useState({});

  const [showGauges, setShowGauges] = useState(true);
  const [moodData, setMoodMeterData] = useState({
    emotional_charge: 0,
    autonomy_motivation: 0,
    competence_motivation: 0,
    relatedness_motivation: 0,
  });

  const browserRecognitionRef = useRef(null);
  const [isBrowserRecording, setIsBrowserRecording] = useState(false);

  //handle first message for creating new conversationid or not in backend
  const [tempConversationId, setTempConversationId] = useState(null);

  //--------------------------------------
  // Audio Volume Detection for Mic Button
  //--------------------------------------
  let audioContext, analyser, microphone, javascriptNode;

  const startMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      levelStreamRef.current = stream;
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      microphone = audioContext.createMediaStreamSource(stream);
      javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);

      analyser.smoothingTimeConstant = 0.8;
      analyser.fftSize = 1024;

      microphone.connect(analyser);
      analyser.connect(javascriptNode);
      javascriptNode.connect(audioContext.destination);

      javascriptNode.onaudioprocess = () => {
        const array = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(array);
        const values = array.reduce((a, b) => a + b, 0);
        const average = values / array.length;
        setAudioLevel(Math.min(1, average / 128)); // normalize between 0 and 1
      };
    } catch (err) {
      console.error('Mic access error:', err);
    }
  };

  const stopMic = () => {
    try {
      if (microphone) {
        try { microphone.disconnect(); } catch (e) {}
      }
      if (javascriptNode) {
        try { javascriptNode.disconnect(); } catch (e) {}
      }
  
      // ✅ Only close if not already closed
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => {});
      }
    } finally {
      // ✅ Clear references so repeated calls won't try to close again
      microphone = null;
      javascriptNode = null;
      audioContext = null;
  
      if (levelStreamRef.current) {
        try { levelStreamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
        levelStreamRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (!isRecording) return;
  
    startMic();
  
    return () => {
      stopMic();
    };

  }, [isRecording]);

  //--------------------------------------
  //  Set up variables for states/local
  //--------------------------------------

    // Conversation ID
  const [conversationId, setConversationId] = useState(() =>
    localStorage.getItem('conversationId') || location.state?.conversationId || null
  );

  // Custom Scenario Flag
  const [isCustomScenario, setIsCustomScenario] = useState(() =>
    JSON.parse(localStorage.getItem('isCustomScenario')) ??
    location.state?.isCustomScenario ??
    false
  );
  // Scenario ID
  const [scenarioId, setScenarioId] = useState(() =>
    localStorage.getItem('scenarioId') || location.state?.scenarioId || null
  );

  // Difficulty Level
  const [difficultyLevel, setDifficultyLevel] = useState(() =>
    localStorage.getItem('difficultyLevel') || location.state?.difficultyLevel || null
  );

  const [firstMessage, setFirstMessage] = useState(() => {
    const storedFirstMessage = localStorage.getItem('firstMessage');
    if (storedFirstMessage !== null) {
      return JSON.parse(storedFirstMessage); // Parse as boolean
    }
    return !!location.state?.fromPage; // True if routing, false if refresh
  });

  // Continued Conversation Flag
  const [isContinued, setIsContinued] = useState(() =>
    Boolean(localStorage.getItem('conversationId') || location.state?.conversationId)
  );

  //------------------------------------
  //    NEW SCENARIO FETCH CODE
  //------------------------------------

  //set variables to local storage as applicable
  useEffect(() => {
    if (conversationId && localStorage.getItem('conversationId') !== conversationId) {
      localStorage.setItem('conversationId', conversationId);
    }
    const storedCustomScenario = JSON.parse(localStorage.getItem('isCustomScenario'));
    if (isCustomScenario !== storedCustomScenario) {
      localStorage.setItem('isCustomScenario', JSON.stringify(isCustomScenario));
    }
    if (scenarioId && localStorage.getItem('scenarioId') !== scenarioId) {
      localStorage.setItem('scenarioId', scenarioId);
    }
    if (difficultyLevel && localStorage.getItem('difficultyLevel') !== difficultyLevel) {
      localStorage.setItem('difficultyLevel', difficultyLevel);
    }
    const storedFirstMessage = JSON.parse(localStorage.getItem('firstMessage'));
    if (firstMessage !== storedFirstMessage) {
      localStorage.setItem('firstMessage', JSON.stringify(firstMessage));
    }
  }, [conversationId, isCustomScenario, scenarioId, difficultyLevel, firstMessage]);

  //useeffect to clear local variables if there is an unmount or change except refresh
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const isRefresh = window.performance.getEntriesByType('navigation')[0]?.type === 'reload';
      if (!isRefresh) {
        console.log("Clearing simulator-specific local storage variables...");
        localStorage.removeItem('conversationId');
        localStorage.removeItem('isCustomScenario');
        localStorage.removeItem('scenarioId');
        localStorage.removeItem('difficultyLevel');
        localStorage.removeItem('firstMessage');
      }
    };
  
    // Add the beforeunload event listener
    window.addEventListener('beforeunload', handleBeforeUnload);
  
    // Cleanup function
    return () => {
      console.log("Navigating away, clearing local storage...");
      localStorage.removeItem('conversationId');
      localStorage.removeItem('isCustomScenario');
      localStorage.removeItem('scenarioId');
      localStorage.removeItem('difficultyLevel');
      localStorage.removeItem('firstMessage');
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const fetchScenarioData = async () => {
      setIsLoadingScenario(true);
  
      try {
        // ✅ Ensure scenarioId & conversationId are retrieved from localStorage if missing
        const storedScenarioId = scenarioId || localStorage.getItem('scenarioId');
        const storedConversationId = conversationId || localStorage.getItem('conversationId');
        const storedDifficultyLevel = Number(difficultyLevel || localStorage.getItem('difficultyLevel'));
        const storedIsCustomScenario = isCustomScenario !== null ? isCustomScenario : JSON.parse(localStorage.getItem('isCustomScenario'));

        // ✅ Prevent fetch if there's no valid scenario or conversation ID
        if (!storedScenarioId && !storedConversationId) {
          console.error("No scenario or conversation ID available.");
          setIsLoadingScenario(false);
          return;
        }

        const payload = {
          conversationId: storedConversationId,
          isCustomScenario: storedIsCustomScenario,
          isContinued: isContinued,
          scenarioId: storedScenarioId,
          difficultyLevel: storedDifficultyLevel,
        };

        // Send request to the backend
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/conversations/get-scenario`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          const data = await response.json();
          
          console.log('Data Content:', data);

          if (data.dials) {
            // Set the gauges to the latest scores
            setMoodMeterData({
              emotional_charge: data.dials.emotional_charge.slice(-1)[0] || 0,
              autonomy_motivation: data.dials.autonomy_motivation.slice(-1)[0] || 0,
              competence_motivation: data.dials.competence_motivation.slice(-1)[0] || 0,
              relatedness_motivation: data.dials.relatedness_motivation.slice(-1)[0] || 0,
            });
          }
         
          setTempConversationId(data.tempConversationId);
          setScenarioData(data);

          // Update scenarioId and difficultyLevel from the data
          if (data.scenarioId) {
            setScenarioId(data.scenarioId); // Ensure this updates the state
          }
          if (data.content?.difficulty) {
            setDifficultyLevel(data.content.difficulty); // Update the difficulty level
          }

          // --------------------------------------
          //   Set up Profile Content for Display
          // --------------------------------------

          const trustLevelMap = {
            "High head and heart trust": { head: "High", heart: "High" },
            "High head and medium heart trust": { head: "High", heart: "Medium" },
            "High head and low heart trust": { head: "High", heart: "Low" },
            "Medium head and high heart trust": { head: "Medium", heart: "High" },
            "Medium head and heart trust": { head: "Medium", heart: "Medium" },
            "Medium head and low heart trust": { head: "Medium", heart: "Low" },
            "Low head and high heart trust": { head: "Low", heart: "High" },
            "Low head and medium heart trust": { head: "Low", heart: "Medium" },
            "Low head and heart trust": { head: "Low", heart: "Low" },
          };

          const trustPrefix = data?.profiles?.trustDescription?.split(':')[0];
          setTrustLevels(trustLevelMap[trustPrefix]);

          const dispositionValueMap = {
            ew: {
              "Highly Engaging": { value: 1, left: "Engage", right: "Withdraw" },
              "Engaging": { value: 2, left: "Engage", right: "Withdraw" },
              "Balanced": { value: 3, left: "Engage", right: "Withdraw" },
              "Withdrawing": { value: 4, left: "Engage", right: "Withdraw" },
              "Highly Withdrawing": { value: 5, left: "Engage", right: "Withdraw" },
            },
            ac: {
              "Highly Accepting": { value: 1, left: "Accepting", right: "Controlling" },
              "Accepting": { value: 2, left: "Accepting", right: "Controlling" },
              "Balanced": { value: 3, left: "Accepting", right: "Controlling" },
              "Controlling": { value: 4, left: "Accepting", right: "Controlling" },
              "Highly Controlling": { value: 5, left: "Accepting", right: "Controlling" },
            },
            ei: {
              "Highly Extroverted": { value: 1, left: "Extroverted", right: "Introverted" },
              "Extroverted": { value: 2, left: "Extroverted", right: "Introverted" },
              "Balanced": { value: 3, left: "Extroverted", right: "Introverted" },
              "Introverted": { value: 4, left: "Extroverted", right: "Introverted" },
              "Highly Introverted": { value: 5, left: "Extroverted", right: "Introverted" },
            },
            di: {
              "Highly Direct": { value: 1, left: "Direct", right: "Indirect" },
              "Direct": { value: 2, left: "Direct", right: "Indirect" },
              "Balanced": { value: 3, left: "Direct", right: "Indirect" },
              "Indirect": { value: 4, left: "Direct", right: "Indirect" },
              "Highly Indirect": { value: 5, left: "Direct", right: "Indirect" },
            },
            pj: {
              "Highly Percieving": { value: 1, left: "Perceiving", right: "Judging" },
              "Percieving": { value: 2, left: "Perceiving", right: "Judging" },
              "Balanced": { value: 3, left: "Perceiving", right: "Judging" },
              "Judging": { value: 4, left: "Perceiving", right: "Judging" },
              "Highly Judging": { value: 5, left: "Perceiving", right: "Judging" },
            },
            rt: {
              "Highly Risk-Taking": { value: 1, left: "Risk-Taking", right: "Risk-Assessing" },
              "Risk-Taking": { value: 2, left: "Risk-Taking", right: "Risk-Assessing" },
              "Balanced": { value: 3, left: "Risk-Taking", right: "Risk-Assessing" },
              "Risk-Assessing": { value: 4, left: "Risk-Taking", right: "Risk-Assessing" },
              "Highly Risk-Assessing": { value: 5, left: "Risk-Taking", right: "Risk-Assessing" },
            },
            op: {
              "Highly Optimistic": { value: 1, left: "Optimistic", right: "Pessimistic" },
              "Optimistic": { value: 2, left: "Optimistic", right: "Pessimistic" },
              "Balanced": { value: 3, left: "Optimistic", right: "Pessimistic" },
              "Pessimistic": { value: 4, left: "Optimistic", right: "Pessimistic" },
              "Highly Pessimistic": { value: 5, left: "Optimistic", right: "Pessimistic" },
            },
            co: {
              "Highly Change-Oriented": { value: 1, left: "Change-Oriented", right: "Continuity-Oriented" },
              "Change-Oriented": { value: 2, left: "Change-Oriented", right: "Continuity-Oriented" },
              "Balanced": { value: 3, left: "Change-Oriented", right: "Continuity-Oriented" },
              "Continuity-Oriented": { value: 4, left: "Change-Oriented", right: "Continuity-Oriented" },
              "Highly Continuity-Oriented": { value: 5, left: "Change-Oriented", right: "Continuity-Oriented" },
            },
            tp: {
              "Highly Task-Focused": { value: 1, left: "Task-Focused", right: "People-Focused" },
              "Task-Focused": { value: 2, left: "Task-Focused", right: "People-Focused" },
              "Balanced": { value: 3, left: "Task-Focused", right: "People-Focused" },
              "People-Focused": { value: 4, left: "Task-Focused", right: "People-Focused" },
              "Highly People-Focused": { value: 5, left: "Task-Focused", right: "People-Focused" },
            }
          };

          const profileDescriptions = data?.profiles || {};
          const tempDispositionLevels = {};
          
          Object.entries(profileDescriptions).forEach(([key, description]) => {
            if (!description || typeof description !== 'string') return;
            if (key.endsWith("Description") && key.length === 13) {
              const traitKey = key.slice(0, 2); // e.g., 'ew'
              const label = description.split(":")[0]; // e.g., 'Balanced'
          
              const traitMap = dispositionValueMap[traitKey];
              if (traitMap && traitMap[label]) {
                tempDispositionLevels[traitKey] = {
                  ...traitMap[label],
                  fullDescription: description, // For tooltip
                };
              }
            }
          });
          
          setDispositionLevels(tempDispositionLevels);



          setConversation(data.conversationHistory || []);

        } else {
          console.error('Failed to fetch scenario data:', await response.text());
        }
      } catch (error) {
        console.error('Error fetching scenario data:', error);
      } finally {
        setIsLoadingScenario(false);
      }
    };
  
    if (scenarioId || conversationId) {
      fetchScenarioData();
    } else {
      console.error('No scenario selected. Please go back and select a scenario.');
    }
  }, [location.state]);
  
  // Log tempConversationId when it updates
  useEffect(() => {
    if (tempConversationId) {
      console.log('got the temp id on mount:', tempConversationId);
    }
  }, [tempConversationId]);

  //-----------------------------------


  // Fetch userId from localStorage when the component mounts
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      console.error("User ID not found in localStorage");
      // Optionally handle what happens when userId is not found, like redirect to login
    }
  }, []); // This runs once when the component mounts
  
  useEffect(() => {
    // keep a ref in sync for websocket callback closures
    ttsPlayingRef.current = isTtsPlaying;
  }, [isTtsPlaying]);
  
  useEffect(() => {
    if (!audioUrl) return;
  
    (async () => {
      await pauseSttForTts();
      try {
        const audio = new Audio(audioUrl);
        await new Promise((resolve, reject) => {
          audio.onended = resolve;
          audio.onerror = reject;
          audio.play().catch(reject);
        });
      } catch (err) {
        console.error('Playback failed:', err);
      } finally {
        await resumeSttAfterTts();
      }
    })();
  }, [audioUrl]);

  // Retrieve the firstName from localStorage when the component mounts
  useEffect(() => {
    const storedFirstName = localStorage.getItem('firstName');
    if (storedFirstName) {
      setFirstName(storedFirstName);
    }
  }, []);

//------------------------------------
//       Tutorial Functions
//------------------------------------  
  useEffect(() => {
    const checkTutorialStatus = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/conversations/tutorial-status`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (!response.ok) throw new Error("Failed to fetch tutorial status");

            const data = await response.json();
            if (!data.hasViewedTutorial) {
                setShowTutorial(true);
            }
        } catch (error) {
            console.error("Error fetching tutorial status:", error);
        }
    };

  checkTutorialStatus();
  }, []);

  // ✅ Handle moving to the next tutorial step
  const handleNextTutorial = () => {
    if (tutorialStep < 7) {
      setTutorialStep(prevStep => prevStep + 1);
    } else {
      handleFinishTutorial();
    }
  };

  // ✅ Handle dismissing the tutorial early
  const handleDismissTutorial = () => {
    setShowTutorial(false);
    handleFinishTutorial();
  };

  // ✅ Handle completing the tutorial and updating the database
  const handleFinishTutorial = async () => {
    setShowTutorial(false);
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/conversations/tutorial-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ hasViewedTutorial: true })
      });
    } catch (error) {
      console.error("Error updating tutorial status:", error);
    }
  };

  const baseButtonStyle = {
    padding: '10px 15px',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  };
  
  const TutorialStepBox = ({ step, showTutorial, currentStep, position, title, text, onNext, onBack, onDismiss }) => {
    if (!showTutorial || currentStep !== step) return null;
  
    return (
      <div style={{
        position: 'absolute',
        ...position, // e.g., top: '300px', left: '50px'
        background: '#e0f2ff',
        padding: '15px',
        borderRadius: '10px',
        boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.4)',
        maxWidth: '320px',
        textAlign: 'center',
        zIndex: 3000
      }}>
        <h3 style={{ color: '#007BFF' }}>{title}</h3>
        <p style={{ color: '#003366' }}>{text}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
          {onBack && (
            <button
              onClick={onBack}
              style={{ ...baseButtonStyle, background: '#6c757d' }}
            >
              Back
            </button>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              style={{ ...baseButtonStyle, background: '#dc3545' }}
            >
              Dismiss
            </button>
          )}
          {onNext && (
            <button
              onClick={onNext}
              style={{ ...baseButtonStyle, background: '#007BFF' }}
            >
              Next
            </button>
          )}
        </div>
      </div>
    );
  };
  


//------------------------------------
//           TOP BUTTONS CODE
//------------------------------------

const handleRestartConversation = async () => {
  setConversation([]); // Clear conversation history (UI-only state)
  setFirstMessage(true); // Reset firstMessage to true
  setConversationId(null); // Clear conversation ID (ensure it's no longer "continued")
  setIsContinued(false); // Explicitly mark as a fresh conversation
  setIsLoadingScenario(true); // Show loading state
  
  try {
    const payload = {
      conversationId: null,
      isCustomScenario,
      isContinued: false,
      scenarioId,
      difficultyLevel,
  };

  console.log('payload for restart:', payload);
    // Send request to the backend to restart the conversation
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/conversations/get-scenario`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const data = await response.json();

      // Update the state with the new conversation data
      setScenarioData(data); // Update scenario data
      setTempConversationId(data.tempConversationId); // Update tempConversationId for new conversation

    } else {
      console.error('Failed to restart conversation:', await response.text());
    }
  } catch (error) {
    console.error('Error restarting conversation:', error);
  } finally {
    setIsLoadingScenario(false); // Hide the loading indicator
  }
};

const handleFinishConversation = async () => {
  const userId = localStorage.getItem('userId'); // Retrieve userId from localStorage
  const token = localStorage.getItem('token'); // Retrieve the token
  const useGemini = localStorage.getItem('useGemini') === 'true'; // ✅ Retrieve useGemini as a boolean

  if (!userId || !token) {
    console.error('User ID or Token not found in localStorage');
    setStatusMessage('Error: User not logged in.');
    return;
  } 

  setIsGeneratingFeedback(true);
  let softProgress = 0;
  const interval = setInterval(() => {
      if (softProgress < 99) {
          setProgress((prev) => Math.min(prev + 1, 99));
          softProgress++;
      }
  }, 1200); // Increment progress by 1% every second
      
  // ✅ Determine which conversation ID to use
  const finalConversationId = conversationId || tempConversationId;

  // ✅ Check if it's a continued conversation with no messages
  const isNoMessageAnalysis = isContinued && tempConversationId && !conversationId;

  console.log("🚀 Sending Analysis Request with:");
  console.log("  isNoMessageAnalysis:", isNoMessageAnalysis);
  console.log("  finalConversationId:", finalConversationId);
  console.log("  isContinued:", isContinued);
  console.log("  tempConversationId:", tempConversationId);
  console.log("  conversationId:", conversationId);

  try {
    // ✅ Send `noMessageAnalysis` as a query parameter
    const eventSource = new EventSource(
      `${process.env.REACT_APP_API_BASE_URL}/api/feedback/generate-report-data/${finalConversationId}?token=${token}&noMessageAnalysis=${isNoMessageAnalysis}&useGemini=${useGemini}`
    );

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Progress update:', data.progress); // Log progress
      const backendProgress = data.progress;

      // Update progress bar based on backend updates
      if (backendProgress > softProgress) {
        setProgress(backendProgress);
        softProgress = backendProgress; // Sync with backend
      }

      if (backendProgress === 100) {
        // Close the EventSource and clear the interval
        eventSource.close();
        clearInterval(interval);

        setProgress(100);
        setIsGeneratingFeedback(false);
        setStatusMessage('Feedback generation complete.');
        navigate('/convo'); // Navigate after completion
      }
    };

    eventSource.onerror = (error) => {
      console.error('Error with progress tracking:', error);
      setStatusMessage('Error tracking progress.');
      setIsGeneratingFeedback(false);
      eventSource.close();
    };

  
  } catch (error) {
    console.error('Error saving conversation:', error);
    setStatusMessage('Error saving conversation.');
    setProgress(100); // Ensure progress completes on error
    setIsGeneratingFeedback(false); // Ensure the dialog is hidden on error
  }
};

//---------------------------------------

//------------------------------------
//      PAGE FUNCTIONS FOR ACTIVITY
//------------------------------------

  // Start resizing
  const handleMouseDown = () => {
    setIsResizing(true);
  };

  // Stop resizing
  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add event listeners for mouse events
  useEffect(() => {
    // Handle mouse movement when resizing
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newLeftWidth = (e.clientX / window.innerWidth) * 100; // Calculate width as a percentage
      // Calculate minimum width in percentage for the left side
      const minLeftWidth = (600 / window.innerWidth) * 100; // 600px converted to percentage

      if (newLeftWidth > minLeftWidth && newLeftWidth < 80) { // Optional min/max limits
        setLeftWidth(newLeftWidth);
        }
      }
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    //Cleanup function
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Function to render description with line breaks as separate paragraphs
  const renderDescription = (desc) => {
    if (!desc) return null; // Return null or an appropriate fallback if desc is not provided
    return desc.split('\r\n').map((paragraph, index) => (
      <p key={index}>{paragraph}</p>
    ));
  };


  //------------------------------------
  //       VOICE TO TEXT
  //------------------------------------
  const punctuateFinalTranscript = async (rawText) => {
    const payload = { rawText };
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/conversations/add-punctuation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });
  
      const data = await response.json();
      return data.punctuated || rawText;
    } catch (error) {
      console.error('Punctuation API error:', error);
      return rawText;
    }
  };

  // ------------------------------------
  //  Suppress STT while TTS is playing
  // ------------------------------------
  const pauseSttForTts = async () => {
    if (ttsPlayingRef.current) return;
  
    setIsTtsPlaying(true);
    ttsPlayingRef.current = true;
    sttPausedForTtsRef.current = { socket: false, browser: false };
  
    // Only suppress STT if the mic was ON when the user hit Send
    if (!micWasOnAtSendRef.current) {
      return;
    }
  
    // If using Google STT over websocket (MediaRecorder), MUTE it (don't pause)
    if (mediaRecorderRef.current && micStreamRef.current) {
      sttPausedForTtsRef.current.socket = true;
      try {
        micStreamRef.current.getAudioTracks().forEach((t) => (t.enabled = false));
      } catch (e) {
        console.warn('Could not mute mic tracks:', e);
      }
    }
  
    // If using browser STT (webkitSpeechRecognition), stop it.
    if (isBrowserRecording) {
      sttPausedForTtsRef.current.browser = true;
      stopBrowserSTT();
    }
  };
  
  const resumeSttAfterTts = async () => {
    if (!ttsPlayingRef.current) return;
  
    setIsTtsPlaying(false);
    ttsPlayingRef.current = false;
  
    // Only restore STT if the mic was ON when the user hit Send
    if (!micWasOnAtSendRef.current) {
      sttPausedForTtsRef.current = { socket: false, browser: false };
      return;
    }
  
    // Resume websocket STT if we muted it
    if (sttPausedForTtsRef.current.socket && micStreamRef.current) {
      try {
        micStreamRef.current.getAudioTracks().forEach((t) => (t.enabled = true));
      } catch (e) {
        console.warn('Could not unmute mic tracks:', e);
      }
    }
  
    // Resume browser STT if we stopped it.
    if (sttPausedForTtsRef.current.browser) {
      try {
        await startBrowserSTT();
      } catch (e) {
        console.warn('Could not restart browser STT:', e);
      }
    }
  
    sttPausedForTtsRef.current = { socket: false, browser: false };

    // ✅ If mic was ON at send, but it got stopped anyway, restart it.
    if (micWasOnAtSendRef.current) {
      const shouldUseBrowser = isBrowserSTTCompatible();

      if (shouldUseBrowser) {
        if (!isBrowserRecording && !ttsPlayingRef.current) {
          try { await startBrowserSTT(); } catch (e) {}
        }
      } else {
        if (!isRecording && !ttsPlayingRef.current) {
          try { await toggleRecording(); } catch (e) {}
        }
      }
    }

    // ✅ Websocket STT recovery: if the server timed out during TTS, restart after playback.
    if (!isBrowserSTTCompatible() && micWasOnAtSendRef.current) {
      const needsRestart =
        socketTimedOutDuringTtsRef.current ||
        !socketRef.current ||
        socketRef.current.disconnected ||
        !isStreamReady;

      if (needsRestart && !ttsPlayingRef.current) {
        socketTimedOutDuringTtsRef.current = false;
        try {
          // toggleRecording starts if not recording; stopRecording already ran on timeout
          await toggleRecording();
        } catch (e) {
          console.warn('Could not restart websocket STT after TTS:', e);
        }
      }
    }

  };


  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      
      // Stop each track in the stream to fully release the mic
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;

      // Clear refs to ensure they don't hold stale state
      mediaRecorderRef.current = null;
      socketRef.current?.disconnect();
      socketRef.current = null;

      // Update state
      setIsRecording(false);
      setIsStreamReady(false);
    }
  }, []);

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording();
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = stream;
        const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm; codecs=opus' });

        mediaRecorder.ondataavailable = event => {
          if (event.data.size > 0 && socketRef.current) {
            socketRef.current.emit('audioMessage', event.data);
          }
        };
        
        // Start the media recorder
        mediaRecorder.start(250); // Send audio chunks every 250ms

        // Initialize WebSocket connection
        socketRef.current = io(`${process.env.REACT_APP_API_BASE_URL}`);
        socketRef.current.on('transcript', data => {
          if (ttsPlayingRef.current) return; // 🔇 ignore any echo while TTS is playing
          dispatch({
            type: data.isFinal ? 'append-final' : 'update-interim',
            payload: data.transcription,
          });
        });
        socketRef.current.on('timeout', () => {
          if (ttsPlayingRef.current) {
            console.log('⏳ STT timeout during TTS playback — will restart after TTS');
            socketTimedOutDuringTtsRef.current = true;
        
            // Stop immediately so we don't stay "green but dead"
            stopRecording();
            return;
          }
        
          stopRecording();
        });

      // Listen for "stream-ready" event
      socketRef.current.on('stream-ready', () => {
        console.log('Google stream is ready');
        setIsStreamReady(true); // Update state when the stream is ready
      });

        // Update refs and state
        mediaRecorderRef.current = mediaRecorder;
        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone or setting up recording:', error);
      }
    }
  }, [isRecording, stopRecording]);

  const stopBrowserSTT = () => {
    try {
      if (browserRecognitionRef.current) {
        console.log('🛑 Stopping browser STT (explicit)');
        browserRecognitionRef.current.onresult = null;
        browserRecognitionRef.current.onerror = null;
        browserRecognitionRef.current.onend = null;
        browserRecognitionRef.current.stop();
      }
    } catch (e) {
      console.warn('stopBrowserSTT error:', e);
    } finally {
      browserRecognitionRef.current = null;
      setIsBrowserRecording(false);
      stopMic();
    }
  };
  
  const startBrowserSTT = async () => {
    console.log('🎙️ Starting browser STT (explicit)');
  
    await startMic();
  
    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
  
    browserRecognitionRef.current = recognition;
    setIsBrowserRecording(true);
  
    recognition.onresult = async (event) => {
      // 🔇 Ignore STT results during TTS playback or while a message is processing
      if (ttsPlayingRef.current || state.isMessageBeingProcessed) return;
    
      let interimTranscript = '';
      let finalTranscript = '';
    
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalTranscript += transcript;
        else interimTranscript += transcript;
      }
    
      if (interimTranscript) {
        dispatch({ type: 'update-interim', payload: interimTranscript });
      }
    
      if (finalTranscript) {
        const punctuated = await punctuateFinalTranscript(finalTranscript);
        dispatch({ type: 'append-final', payload: punctuated });
      }
    };
  
    recognition.onerror = (e) => {
      console.error("Browser STT Error", e);
      setIsBrowserRecording(false);
      stopMic();
    };
  
    recognition.onend = () => {
      console.log('🧘 Browser STT ended');
      setIsBrowserRecording(false);
      stopMic();
    };
  
    recognition.start();
  };
  
  //set STT for Chrome/Edge Browsers
  const toggleBrowserSTT = async () => {
    if (isBrowserRecording) {
      stopBrowserSTT();
      return;
    }
    await startBrowserSTT();
  };
  

  //turn off mic when leaving page
  useEffect(() => {
    const handleBeforeUnload = () => {
      stopRecording(); // ✅ Stop mic if the page is refreshed or closed
    };
  
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      stopRecording(); // ✅ Also stop mic when leaving the page
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

// Effect to adjust height whenever the displayed text changes
useEffect(() => {
  adjustTextareaHeight();
}, [state.displayedText]);

const adjustTextareaHeight = () => {
  const textarea = textareaRef.current;
  if (textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }
};

//------------------------------------------

// Define the helper function outside handleSendMessage but within the component
const updateMoodData = (data) => {
  setMoodMeterData({
    emotional_charge: data.emotional_charge || 0,
    autonomy_motivation: data.autonomy_motivation || 0,
    competence_motivation: data.competence_motivation || 0,
    relatedness_motivation: data.relatedness_motivation || 0,
  });
};

//------------------------------------
//       SEND MESSAGES TO BACKEND
//------------------------------------
// Function to simulate typing animation
useEffect(() => {
  if (isTyping) {
      const interval = setInterval(() => {
          setTypingDots((prevDots) => {
              if (prevDots === '...') return '.';
              return prevDots + '.';
          });
      }, 250); // Update dots every 500ms
      return () => clearInterval(interval);
  } else {
      setTypingDots(''); // Clear dots when not typing
  }
}, [isTyping]);

const handleSendMessage = async () => {
  //check if mic is on or off at message send
  const micIsCurrentlyOn =
  isBrowserSTTCompatible()
    ? isBrowserRecording
    : isRecording;

  micWasOnAtSendRef.current = micIsCurrentlyOn;

  //block stt after send message button is pressed
  dispatch({ type: 'set-processing-flag', payload: true });

  // Return early if there is no message to send
  let currentMessage = state.displayedText.trim();
  if (!currentMessage) {
    currentMessage = '...';  // Intentional silence
  }
  
  // Clear the textarea after sending
  dispatch({ type: 'edit', payload: '' });

  //pull in useGemini satus
  const useGemini = JSON.parse(localStorage.getItem('useGemini')) || false;

  // Add typing indicator placeholder to the conversation
  setConversation((prevConversation) => [
    ...prevConversation,
    { role: 'user', content: currentMessage },
    { role: 'assistant', content: '...' },
  ]);
  setIsTyping(true); // Start the typing indicator

  // Prepare the payload for the API request
  const payload = {
    userMessage: currentMessage, // Send the user's message
    conversationId, // Include the conversation ID
    isTtsEnabled, //tts on/off status
    isCustomScenario,
    firstMessage,
    tempConversationId,
    useGemini,
  };

  let ttsStarted = false;
  
  try {
      // Send message to the backend API
      console.log('sending message payload:', payload);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/conversations/messages`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`, // Make sure token is available
          },
          body: JSON.stringify(payload), // Send user's message to the backend
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let bufferedData = ''; // Buffer to accumulate data
      let moodDataReceived = false;
      let firstAudioPlayed = false;

      const playAudioPart = (audioBase64) => {
        const audioUrl = `data:audio/mp3;base64,${audioBase64}`;
        const audio = new Audio(audioUrl);
        return new Promise((resolve, reject) => {
          audio.onended = resolve;
          audio.onerror = reject;
          audio.play().catch(reject);
        });
      };

      while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          // Decode the current chunk
          bufferedData += decoder.decode(value); // Accumulate data

          // Split accumulated data by the delimiter
          const messages = bufferedData.split('\n');
          bufferedData = messages.pop(); // Retain any incomplete chunk

          for (const message of messages) {

            try {
              const data = JSON.parse(message);

              // Update conversationId if provided in the response
              if (data.conversationId && data.conversationId !== conversationId) {
                setConversationId(data.conversationId); // Update the state with the new conversationId
                localStorage.setItem('conversationId', data.conversationId); // Save to localStorage
              }

              if (data.response) {
                setConversation((prevConversation) => [
                    ...prevConversation.slice(0, -1), // Remove typing placeholder
                    { role: 'assistant', content: data.response },
                ]);
              }

              if (!firstAudioPlayed && data.audioPart1) {
                  // Play the first audio part
                  firstAudioPlayed = true;

                  if (!ttsStarted) {
                    await pauseSttForTts();
                    ttsStarted = true;
                  }

                  await playAudioPart(data.audioPart1);
              }

              // Play the second audio part if it exists
              if (data.audioPart2) {
                if (!ttsStarted) {
                  await pauseSttForTts();
                  ttsStarted = true;
                }
                
                await playAudioPart(data.audioPart2);
              }

              if (data.moodData && !moodDataReceived) {
                moodDataReceived = true;
                updateMoodData(data.moodData);
              }
          } catch (error) {
            console.error('Error parsing chunk as JSON:', error);
          }
        }
      }

      if (ttsStarted) {
        await resumeSttAfterTts();
      }

      // After the first message, set firstMessage to false
      if (firstMessage) {
        setFirstMessage(false); // Update state
        localStorage.setItem('firstMessage', JSON.stringify(false)); // Save to localStorage
      }

      setIsTyping(false); // Stop the typing indicator
      dispatch({ type: 'set-processing-flag', payload: false }); // Re-enable STT updates
      dispatch({ type: 'reset' }); // 🚫 Clear interim & text completely

  } catch (error) {
      console.error('Error communicating with the backend:', error);
      setStatusMessage('Error sending message.');

      if (ttsStarted) {
        try { await resumeSttAfterTts(); } catch (e) {}
      }

      // Remove typing indicator on error
      setConversation((prevConversation) => prevConversation.slice(0, -1)); // Remove typing indicator
      setIsTyping(false);
      dispatch({ type: 'set-processing-flag', payload: false }); // Re-enable STT updates
      dispatch({ type: 'reset' }); // 🚫 Clear interim & text completely
  }
};

//------------------------------------
//       Guage Props
//------------------------------------
const getColorFromValue = (value, reverse = false) => {
  const v = reverse ? 100 - value : value;
  const red = Math.min(255, Math.floor((v / 100) * 255));
  const green = Math.min(255, Math.floor(((100 - v) / 100) * 255));
  return `rgb(${red}, ${green}, 0)`;
};

return ( 
<>

{/* //-------------------------------------- */}
{/* // Finish & Analyze Conversation Pop-up  */}
{/* //-------------------------------------- */}
{/* Main Conversation Analyze Modal */}
{isFinishModalOpen && (
  <div
    className="fixed inset-0 flex items-center justify-center z-50"
    onClick={() => setIsFinishModalOpen(false)}
  >
    {/* Background Overlay */}
    <div className="absolute inset-0 bg-black bg-opacity-50"></div>

    {/* Modal Box */}
    <div
      className="relative bg-white rounded-lg shadow-xl p-6 w-[350px] text-center z-10"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Run Conversation Analysis?</h3>
      <p className="text-gray-700 mb-3">
        This will generate a detailed report based on your current conversation.
      </p>
      <p className="text-sm text-gray-600 mb-2">
        <strong className="italic">You can continue the conversation after running the report.</strong><br />
        The report will be available in the <strong>Conversations & Reports</strong> page.
      </p>
      <p className="text-gray-700">Would you like to proceed?</p>

      <div className="flex justify-center gap-4 mt-6">
        <button
          onClick={() => {
            setIsFinishModalOpen(false);
            handleFinishConversation();
          }}
          className="px-4 py-2 bg-green-700 text-white font-bold rounded hover:bg-green-800 transition"
        >
          Yes, Finish & Analyze
        </button>
        <button
          onClick={() => setIsFinishModalOpen(false)}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}


{isNoConversationModalOpen && (
  <div
    className="fixed inset-0 flex items-center justify-center z-50"
    onClick={() => setIsNoConversationModalOpen(false)}
  >
    {/* Background Overlay */}
    <div className="absolute inset-0 bg-black bg-opacity-50"></div>

    {/* Modal Box */}
    <div
      className="relative bg-white rounded-lg shadow-xl p-6 w-[350px] text-center z-10"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-semibold text-gray-800 mb-3">No Conversation to Analyze</h3>
      <p className="text-gray-700">
        You haven't started a conversation yet. Please send a message before running an analysis.
      </p>
      <button
        onClick={() => setIsNoConversationModalOpen(false)}
        className="mt-5 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
      >
        OK
      </button>
    </div>
  </div>
)}

{/* //------------------------------------ */}
{/* //     Restart Conversation Pop-up     */}
{/* //------------------------------------ */}
{isRestartModalOpen && (
  <div
    className="fixed inset-0 flex items-center justify-center z-50"
    onClick={() => setIsRestartModalOpen(false)}
  >
    {/* Background Overlay */}
    <div className="absolute inset-0 bg-black bg-opacity-50"></div>

    {/* Modal Box */}
    <div
      className="relative bg-white rounded-lg shadow-xl p-6 w-[350px] text-center z-10"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Restart Conversation?</h3>
      <p className="text-gray-700 mb-2">
        Your current conversation will be saved and accessible in the <strong>Conversations & Reports</strong> page.
      </p>
      <p className="text-gray-700">Are you sure you want to clear the chat history and restart the conversation?</p>

      <div className="flex justify-center gap-4 mt-6">
        <button
          onClick={() => {
            setIsRestartModalOpen(false);
            handleRestartConversation();
          }}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
        >
          Yes, Restart
        </button>
        <button
          onClick={() => setIsRestartModalOpen(false)}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

{/* //------------------------------------ */}
{/* //       Loading Indicator             */}
{/* //------------------------------------ */}

{isLoadingScenario && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
    <div className="w-[300px] bg-white rounded-lg p-6 text-center shadow-lg">
      <h2 className="text-lg font-semibold text-gray-800 mb-2">Loading Scenario...</h2>
      <p className="text-gray-600">Please wait while we load the scenario details.</p>
    </div>
  </div>
)}


{/*//------------------------------------ */}
{/*//     Feedback Generation Pop up      */}
{/*//------------------------------------ */}

{isGeneratingFeedback && (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
    <div className="w-[300px] bg-white rounded-lg p-6 text-center shadow-lg">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Generating Feedback Report...</h2>

      <div className="w-full h-4 bg-gray-300 rounded-full overflow-hidden mb-2">
        <div
          className="h-full bg-green-500 transition-all duration-200"
          style={{ width: `${progress}%` }}
        ></div>
      </div>

      <p className="text-sm text-gray-700">{progress}%</p>
    </div>
  </div>
)}


{/*//------------------------------------ */}
{/*//      PAGE DESIGN*/}
{/*//------------------------------------*/}
        
<div className="w-full max-w-7xl mx-auto px-4 gap-x-2 flex flex-col [@media(min-width:1160px)]:flex-row overflow-hidden gap-6">  

{showTutorial && (
  <div
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0, 0, 0, 0.6)',
      zIndex: 999,
      pointerEvents: 'auto',
    }}
  />
)}

  <TutorialStepBox
    step={0}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    }}
    title="Welcome to the Simulator"
    text="Let's walk you through some of the key features before you start your conversation."
    onNext={handleNextTutorial}
    onDismiss={handleDismissTutorial}
  />

  <TutorialStepBox
    step={1}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      bottom: '40%',
      right: '50%'
    }}
    title="Scenario Details"
    text="This section provides key information about the employee and their background."
    onNext={handleNextTutorial}
    onBack={() => setTutorialStep(prev => prev - 1)}
    onDismiss={handleDismissTutorial}
  />

  <TutorialStepBox
    step={2}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      top: '40%',
      right: '10%'
    }}
    title="Conversation Dashboard"
    text="This is where you will interact with the employee throughout the simulation. Click next to see more details about this interface."
    onNext={handleNextTutorial}
    onBack={() => setTutorialStep(prev => prev - 1)}
    onDismiss={handleDismissTutorial}
  />

  <TutorialStepBox
    step={3}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      top: '80px',
      left: '270px'
    }}
    title="Enable Employee Audio"
    text="Use this checkbox to enable or disable the employee's response using spoken audio. Either way, you will see the response in text in the chat history."
    onNext={handleNextTutorial}
    onBack={() => setTutorialStep(prev => prev - 1)}
    onDismiss={handleDismissTutorial}
  />

  <TutorialStepBox
    step={4}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      top: '300px',
      right: '60%' // centers the box horizontally relative to the left panel
    }}
    title="Mood & Motivation Gauges"
    text="These gauges show real-time measurements of the employee’s feelings of emotions and motivation to provide insight on how the conversation is going. If you don't want to use the gauges, you can hide them by clicking the show gauges checkbox."
    onNext={handleNextTutorial}
    onBack={() => setTutorialStep(prev => prev - 1)}
    onDismiss={handleDismissTutorial}
  />

  <TutorialStepBox
    step={5}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      bottom: '300px',
      left: 'calc(25% + 50px)' // estimate center of left panel, adjust as needed
    }}
    title="Message Input Box"
    text="The message input box is where your messages to the employee will go. You can either type your messages directly or you can say your message to communicate with the employee. Once your message is done, click send. Next we will learn more about how to have a verbal conversation."
    onNext={handleNextTutorial}
    onBack={() => setTutorialStep(prev => prev - 1)}
    onDismiss={handleDismissTutorial}
  />

  <TutorialStepBox
    step={6}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      bottom: '280px',
      left: 'calc(25% + 80px)' // estimated mic button area, adjust as needed
    }}
    title="Microphone Button"
    text="To say your messages audibly, click this microphone button, wait for it to turn red, speak your message, and then hit send (for first time use, you may need to authorize use of the microphone in the top left of your browser)."
    onNext={handleNextTutorial}
    onBack={() => setTutorialStep(prev => prev - 1)}
    onDismiss={handleDismissTutorial}
  />

  <TutorialStepBox
    step={7}
    showTutorial={showTutorial}
    currentStep={tutorialStep}
    position={{
      bottom: '160px',
      left: 'calc(25% - 40px)' // adjust this value to align above the "Finish & Analyze" button
    }}
    title="Finish & Analyze"
    text="Once a conversation is complete, it's time to run the report and analyze the conversation. Click this button to generate a conversation report."
    onBack={() => setTutorialStep(prev => prev - 1)}
    onDismiss={handleDismissTutorial}
  />


  {/* LEFT PANEL */}
  <div
    className="flex-1 min-w-[370px] flex flex-col h-[calc(100vh-60px)] overflow-hidden min-h-0 bg-white shadow-lg rounded-lg"
    style={{
      position: tutorialStep === 2 ? 'relative' : 'static',
      zIndex: tutorialStep === 2 ? 1000 : 'auto',
      backgroundColor: tutorialStep === 2 ? 'white' : undefined,
      boxShadow: tutorialStep === 2 ? '0 0 10px rgba(0, 0, 0, 0.3)' : undefined,
      borderRadius: tutorialStep === 2 ? '8px' : undefined
    }}
  >
    {/* Header with Audio & Gauges Toggles */}
    <div className="px-4 py-2">
      <div className="border-b border-gray-300 pb-2 flex justify-between items-center">
        <h1 className="text-xl font-bold text-gray-800">Conversation</h1>
        
        <div className="flex flex-wrap justify-end space-x-6">
          <label
            className="flex items-center space-x-2 text-sm px-2 py-1 rounded"
            style={{
              position: tutorialStep === 3 ? 'relative' : 'static',
              zIndex: tutorialStep === 3 ? 1000 : 'auto',
              backgroundColor: tutorialStep === 3 ? 'white' : 'transparent',
              boxShadow: tutorialStep === 3 ? '0 0 10px rgba(0,0,0,0.2)' : 'none'
            }}
          >
            <input
              type="checkbox"
              checked={isTtsEnabled}
              onChange={() => setIsTtsEnabled(!isTtsEnabled)}
            />
            <span>Enable Employee Audio</span>
          </label>

          <label className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              checked={showGauges}
              onChange={() => setShowGauges(!showGauges)}
            />
            <span>Show Gauges</span>
          </label>
        </div>
      </div>  
    </div>

    {showGauges && (
        <div
          className="bg-white rounded-lg shadow-md p-4 mx-4"
          style={{
            position: tutorialStep === 4 ? 'relative' : 'static',
            zIndex: tutorialStep === 4 ? 1000 : 'auto',
            backgroundColor: tutorialStep === 4 ? 'white' : undefined,
            boxShadow: tutorialStep === 4 ? '0 0 10px rgba(0, 0, 0, 0.3)' : undefined,
            borderRadius: tutorialStep === 4 ? '8px' : undefined
          }}
        >
          <h3 className="text-center font-semibold mb-4 underline flex justify-center items-center gap-2">
            <span className="cursor-help">
              <TooltipWrapper
                title={
                  <span style={{ fontSize: '.875rem' }}>
                    These gauges provide real-time measures of how the employee is feeling in terms of emotion and motivation (i.e., autonomy, competence, and relatedness). The gauges are updated after each message is sent. Hover over each gauge title for more information.
                  </span>
                }
                arrow
              >
                Employee Emotion and Motivation Gauges
              </TooltipWrapper>
            </span>
            <Tooltip
              title={
                <span style={{ fontSize: '.875rem' }}>
                  These gauges provide real-time measures of how the employee is feeling in terms of emotion and motivation (i.e., autonomy, competence, and relatedness). The gauges are updated after each message is sent. Hover over each gauge title for more information.
                </span>
              }
              arrow
            >
              <HelpOutlineIcon
                style={{
                  fontSize: '14px',
                  color: '#3b82f6', // Tailwind blue-500
                  cursor: 'pointer'
                }}
              />
            </Tooltip>
          </h3>

          <div className="flex flex-nowrap justify-center gap-x-4 overflow-x-auto">
            {[
              { label: "Emotion", value: moodData.emotional_charge, reverse: false },
              { label: "Autonomy", value: moodData.autonomy_motivation, reverse: true },
              { label: "Competence", value: moodData.competence_motivation, reverse: true },
              { label: "Relatedness", value: moodData.relatedness_motivation, reverse: true }
            ].map((g, index) => (
              <div key={index} className="flex flex-col items-center w-[80px]">
                <h4 className="text-sm font-medium mb-1">
                  <GaugeLabelWithTooltip label={g.label} />
                </h4>
                <div className="w-[64px] h-[64px]">
                  <CircularProgressbarWithChildren
                    value={g.value}
                    strokeWidth={12}
                    styles={buildStyles({
                      pathColor: getColorFromValue(g.value, g.reverse),
                      trailColor: "#eee",
                      strokeLinecap: "round",
                    })}
                  >
                    <div
                      className="text-lg font-semibold"
                      style={{ color: getColorFromValue(g.value, g.reverse) }}
                    >
                      {Math.round(g.value)}
                    </div>
                  </CircularProgressbarWithChildren>
                </div>
              </div>
            ))}
          </div>
        </div>
    )}

    {/* Chat History, Message Box, and Mic/Send Container */}
    <div className="flex flex-col h-full overflow-y-auto">
      
      {/* Chat History Container */}
      <div className="flex-1 p-4 min-h-[250px]">
        <div className="bg-white rounded shadow p-4 h-full overflow-y-auto flex flex-col-reverse min-h-[200px]">
          {conversation.slice().reverse().map((msg, index) => (
            <div
              key={index}
              className={`my-2 p-3 rounded-md max-w-[70%] ${
                msg.role === 'user'
                  ? 'bg-green-100 self-end'
                  : 'bg-gray-100 self-start'
              }`}
            >
              <strong>{msg.role === 'user' ? 'You' : scenarioData?.employeeName || 'Employee'}:</strong>{' '}
              {msg.role === 'assistant' && msg.content === '...' ? (
                <span>{typingDots}</span>
              ) : (
                <span>{msg.content}</span>
              )}
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
      </div>

      {/* Input box for sending message */}
      <div
        className="flex items-stretch gap-4 p-4 bg-gray-100 mx-4 rounded-lg shadow-md"
        style={{
          position: tutorialStep === 5 ? 'relative' : 'static',
          zIndex: tutorialStep === 5 ? 1000 : 'auto',
          backgroundColor: tutorialStep === 5 ? 'white' : undefined,
          boxShadow: tutorialStep === 5 ? '0 0 10px rgba(0,0,0,0.3)' : undefined,
          borderRadius: tutorialStep === 5 ? '8px' : undefined
        }}
      >
        {/* Text area for message */}
        <textarea
          ref={textareaRef}
          value={state.displayedText}
          onChange={(e) => dispatch({ type: 'edit', payload: e.target.value })}
          placeholder="Type your message here"
          className="flex-1 text-sm p-2 border border-gray-300 rounded resize-none font-sans h-full"
          style={{ minHeight: '20px', maxHeight: '120px', fontSize: '16px', overflowY: 'auto' }}
          onInput={adjustTextareaHeight}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
        />

        {/* MIC and SEND buttons */}
        <div className="flex flex-col items-center gap-2">
          {/* Define the wave clip path */}
          <svg width="0" height="0">
            <defs>
              <filter id="waveBlur" x="-10%" y="-10%" width="120%" height="120%">
                <feGaussianBlur stdDeviation="1.5" />
              </filter>

              <clipPath id="waveClip" clipPathUnits="objectBoundingBox">
                <path d="
                  M0,0.15
                  Q0.125,0 0.25,0.15
                  T0.5,0.15
                  T0.75,0.15
                  T1,0.15
                  V1 H0 Z"
                  filter="url(#waveBlur)"
                />
              </clipPath>
            </defs>
          </svg>

          {/* Mic Button */}
          <div
            onClick={
              isTtsPlaying
                ? undefined
                : (isBrowserSTTCompatible() ? toggleBrowserSTT : toggleRecording)
            }
            className={`relative w-[60px] h-[60px] rounded-full flex items-center justify-center overflow-hidden border-none
              ${isTtsPlaying ? 'cursor-not-allowed opacity-80' : 'cursor-pointer'}
            `}
            style={{
              backgroundColor: isTtsPlaying
                ? '#f0ad4e' // 🟡 "paused for playback" state
                : (((isRecording && isStreamReady) || isBrowserRecording) ? '#28a745' : 'gray'),
              boxShadow:
                showTutorial && tutorialStep === 6
                  ? '0 0 12px 4px rgba(0, 123, 255, 0.6)'
                  : (isRecording && isStreamReady) || isBrowserRecording
                  ? `0 0 ${10 + audioLevel * 30}px rgba(0,255,0,0.5)`
                  : 'none',
              zIndex: showTutorial && tutorialStep === 6 ? 1000 : 'auto',
              position: 'relative',
            }}
          >
            {!isTtsPlaying && (((isRecording && isStreamReady) || isBrowserRecording)) && (
              <div
                className="absolute bottom-0 left-0 w-full"
                style={{
                  height: `${20 + Math.min(1, audioLevel * 3.5) * 80}%`,
                  background: 'linear-gradient(to top, #8e44ad, #9b59b6, transparent)',
                  transition: 'height 0.1s ease-out',
                  clipPath: 'url(#waveClip)',
                  WebkitClipPath: 'url(#waveClip)',
                  zIndex: 0,
                }}
              />
            )}
            <i className="fas fa-microphone text-white text-xl z-10" />
          </div>

          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={state.isMessageBeingProcessed}
            className={`px-4 py-2 text-white rounded text-sm font-medium transition 
              ${state.isMessageBeingProcessed 
                ? 'bg-purple-400 cursor-not-allowed' 
                : 'bg-purple-700 hover:bg-purple-800'}`}
          >
            {state.isMessageBeingProcessed ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>

    {/* Add buttons for Restart and New Scenario */}
    <div className="flex justify-center gap-4 mt-3 mb-4 px-4">
      <button
        onClick={() => {
          stopRecording();
          if (!isContinued && tempConversationId && !conversationId) {
            setIsNoConversationModalOpen(true);
          } else {
            setIsFinishModalOpen(true);
          }
        }}
        className="w-[180px] px-4 py-3 bg-green-700 text-white rounded font-bold text-base hover:bg-green-800 shadow-lg"
        style={{
          boxShadow:
            showTutorial && tutorialStep === 7
              ? '0 0 12px 4px rgba(0, 123, 255, 0.6)'
              : undefined,
          zIndex: showTutorial && tutorialStep === 7 ? 1000 : 'auto',
          position: showTutorial && tutorialStep === 7 ? 'relative' : 'static',
        }}
      >
        Finish & Analyze
      </button>

      <button
        onClick={() => {
          stopRecording();
          setIsRestartModalOpen(true);
        }}
        className="w-[180px] px-4 py-3 bg-purple-700 text-white rounded font-medium text-base hover:bg-purple-800 shadow-lg"
      >
        Restart
      </button>
    </div>
  </div>

  {/* ----------------------------------- */}
  {/* ----------   Divide  ------------- */}
  {/* ----------------------------------- */}

  {/* RIGHT PANEL */}
  <div
    className="w-full max-w-md 2xl:max-w-lg min-w-[400px] h-[calc(100vh-60px)] overflow-y-auto pr-4"
    style={{
      position: tutorialStep === 1 ? 'relative' : 'static',
      zIndex: tutorialStep === 1 ? 1000 : 'auto',
      backgroundColor: tutorialStep === 1 ? 'white' : 'transparent',
      boxShadow: tutorialStep === 1 ? '0 0 10px rgba(0, 0, 0, 0.3)' : 'none',
      borderRadius: tutorialStep === 1 ? '8px' : undefined
    }}
  >
    <div className="bg-white rounded-lg shadow-lg p-6 w-ful">
      
      {/* Header */}
      <div className="flex justify-between items-center border-b pb-4 mb-6">
        <h2 className="text-xl font-bold text-gray-800">Scenario Details</h2>
        <button
          onClick={() => {
            setShowTutorial(true);
            setTutorialStep(0);
          }}
          className="bg-white border text-sm font-medium px-4 py-2 rounded-full shadow hover:bg-gray-100 transition-colors"
        >
          Tutorial
        </button>
      </div>

      {/* Profile Card */}
      <div
        className="relative bg-zinc-100 p-4 rounded-xl mb-6 flex items-start gap-4 overflow-hidden"
      >
        {/* Background pattern */}
        <span
          aria-hidden="true"
          style={{
            backgroundImage: "url('/patterns/bank-note.svg')",
            backgroundSize: "20%",
            opacity: 0.01,
          }}
          className="pointer-events-none absolute inset-0 bg-repeat z-0"
        />

        {/* Content Layer */}
        <div className="relative z-10 flex items-start gap-4 w-full">
          <div className="h-40 w-40 bg-gray-200 rounded-full overflow-hidden border-2 border-white shadow shrink-0">
            {scenarioData && scenarioData.imageName && (
              <img
                src={`/Profile_Pictures/${scenarioData.imageName}`}
                alt="Scenario Person"
                className="h-full w-full object-cover"
              />
            )}
          </div>
          <div className="flex-1">
            {scenarioData && (
              <>
                <h3 className="text-lg font-semibold text-gray-800">{scenarioData.employeeName}</h3>
                <p className="text-sm text-gray-600">
                  {scenarioData.jobTitle} • {scenarioData.employeeAge} yrs
                </p>
              </>
            )}
            {/* Familiarity */}
            {scenarioData?.profiles?.familiarityDescription && (
              <div className="mt-3 text-sm text-gray-700 flex items-center">
                <span className="flex items-center">
                  Familiarity<ProfileTooltip profileKey="familiarity" />
                </span>
                <span className="mx-1">:</span>
                <TooltipWrapper title={scenarioData.profiles.familiarityDescription} arrow>
                  <span className="font-medium cursor-help">
                    {scenarioData.profiles.familiarityDescription.split(' ')[0]}
                  </span>
                </TooltipWrapper>
              </div>
            )}

            {/* Emotion */}
            {scenarioData?.profiles?.emotionDescription && (
              <div className="mt-1 text-sm text-gray-700 flex items-center">
                <span className="flex items-center">
                  Emotion<ProfileTooltip profileKey="emotion" />
                </span>
                <span className="mx-1">:</span>
                <TooltipWrapper title={scenarioData.profiles.emotionDescription} arrow>
                  <span className="font-medium cursor-help">
                    {scenarioData.profiles.emotionDescription.split(' ')[0]}
                  </span>
                </TooltipWrapper>
              </div>
            )}

            {/* Trust */}
            {trustLevels && (
              <>
                <div className="mt-1 text-sm text-gray-700 flex items-center">
                  <span className="flex items-center">
                    Head Trust<ProfileTooltip profileKey="trust" />
                  </span>
                  <span className="mx-1">:</span>
                  <TooltipWrapper title={scenarioData.profiles.trustDescription}>
                    <span className="font-medium cursor-help">{trustLevels.head}</span>
                  </TooltipWrapper>
                </div>

                <div className="mt-1 text-sm text-gray-700 flex items-center">
                  <span className="flex items-center">
                    Heart Trust<ProfileTooltip profileKey="trust" />
                  </span>
                  <span className="mx-1">:</span>
                  <TooltipWrapper title={scenarioData.profiles.trustDescription}>
                    <span className="font-medium cursor-help">{trustLevels.heart}</span>
                  </TooltipWrapper>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tags */}
      {difficultyLevel && (
        <div className="mb-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            difficultyLevel <= 2
              ? 'bg-green-100 text-green-800'
              : difficultyLevel === 3
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {difficultyLevel <= 2
              ? 'Beginner'
              : difficultyLevel === 3
              ? 'Intermediate'
              : 'Advanced'}
          </span>
        </div>
      )}

      {/* Scenario Context */}
      <div className="mb-6">
        <h4
        className="flex items-center text-sm font-semibold text-gray-600 uppercase mb-2 cursor-pointer"
        onClick={() => setShowContext(prev => !prev)}
        >
          <ChevronDown className={`w-4 h-4 mr-2 transition-transform ${showContext ? "rotate-0" : "-rotate-90"}`} />
          Scenario Context
        </h4>

        {showContext && scenarioData && (
          <div className="bg-white p-4 rounded shadow text-sm text-gray-700 mb-6">
            {scenarioData.content.description
              .replace(/\\r\\n|\\n/g, '\n')  // Convert escaped newlines to real ones
              .split(/\r?\n/)                // Then split on real newlines
              .filter(paragraph => paragraph.trim() !== '')
              .map((paragraph, index) => (
                <p key={index} className="mb-3 whitespace-pre-line">{paragraph}</p>
              ))}
          </div>
        )}
      </div>

      {/* Performance Issues */}
      <div>
        <h4
        className="flex items-center text-sm font-semibold text-gray-600 uppercase mb-2 cursor-pointer"
        onClick={() => setShowIssues(prev => !prev)}
        >
          <ChevronDown className={`w-4 h-4 mr-2 transition-transform ${showIssues ? "rotate-0" : "-rotate-90"}`} />
          Performance Issues
        </h4>

        {showIssues && scenarioData && (
          <div className="space-y-3">
            {scenarioData.content.examples.map((example, index) => (
              <div key={index} className="bg-white p-4 rounded shadow text-sm text-gray-700">
                {example}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Disposition Traits */}
      <div className="mt-6">
        <h4
          className="flex items-center text-sm font-semibold text-gray-600 uppercase mb-2 cursor-pointer"
          onClick={() => setShowDisposition(prev => !prev)}
        >
          <ChevronDown className={`w-4 h-4 mr-2 transition-transform ${showDisposition ? "rotate-0" : "-rotate-90"}`} />
          Disposition
        </h4>

        {showDisposition && (
        <div className="flex flex-col gap-4">
          {Object.entries(dispositionLevels).map(([traitKey, traitData]) => {
            const { value, left, right, fullDescription } = traitData;
            const titleLabel = fullDescription.split(':')[0];

            return (
              <div key={traitKey} className="bg-white shadow pt-2 pb-2 px-4 rounded-md border-l-4 border-purple-200">
                <div className="flex justify-between text-sm font-semibold text-gray-500 mb-2 relative">
                  <span className="flex items-center">
                    {left}
                    <TooltipWrapper title={fullDescription} arrow>
                      <ProfileTooltip profileKey={traitKey} />
                    </TooltipWrapper>
                  </span>

                  <span>{right}</span>
                </div>

                {/* Scale Bar */}
                <div className="relative h-[2px] bg-gradient-to-r from-gray-100 via-gray-300 to-gray-100 rounded-full mt-2 mb-4">
                  {[1, 2, 3, 4, 5].map((step) => (
                    <div
                      key={step}
                      className="absolute" // positions triangle below the line
                      style={{ left: `${(step - 1) * 25}%`, top: '-4px', transform: 'translateX(-50%) rotate(180deg)' }}
                    >
                      <svg
                        width={step === value ? 30 : 24}
                        height={step === value ? 30 : 24}
                        viewBox="0 0 24 24"
                        fill={step === value ? "#8b5cf6" : "#e5e7eb"}
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 16L6 8H18L12 16Z" />
                      </svg>
                    </div>
                  ))}
                </div>

                {/* Label Below Dot */}
                <div className="relative mt-2 h-3">
                  <div
                    className="absolute text-xs font-bold whitespace-nowrap"
                    style={{
                      left:
                        value === 1 ? '0%' :
                        value === 2 ? '20%' :
                        value === 3 ? '50%' :
                        value === 4 ? '80%' :
                        '100%', // for value === 5
                      transform:
                        value === 2 ? 'translateX(5%)' :
                        value === 3 ? 'translateX(-50%)' :
                        value === 4 ? 'translateX(-70%)' :
                        value === 5 ? 'translateX(-100%)' :
                        'none',
                      color: '#8b5cf6'
                    }}
                  >
                    <TooltipWrapper title={fullDescription} arrow>
                      <span>{titleLabel}</span>
                    </TooltipWrapper>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </div>
  </div>
</div>

</>
);
};
export default Simulator;
